import type { Transaction } from '@api/hooks/types/getTransactions';
import { useDeleteTransactionsMultiple } from '@api/hooks/useDeleteTransactionsMultiple';
import { useOperations } from '@api/hooks/useOperations';
import { useInitiatePaymentSigning, useSignPayment } from '@api/hooks/useSigningPayments';
import Alert from '@components/alert';
import { EmptyState } from '@components/EmptyState';
import { PageContainer } from '@components/PageContainer';
import { TfaSigning } from '@components/tfa/TfaSigning';
import {
	Button,
	DropdownPositions,
	Loader,
	Pagination,
	SignIcon,
	Table,
} from '@gurupay/gp-ui-library';
import { usePagination } from '@hooks/usePagination';
import { useSignOperationsTable } from '@pages/operations/hooks/useSignOperationsTable';
import { useQueryClient } from '@tanstack/react-query';
import type { FC } from 'react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { OperationStatus } from 'types/operations';

import { DeleteConfirmModal } from './components/DeleteConfirmModal';
import {
	type OperationsFilterParams,
	OperationsFiltersModal,
} from './components/OperationsFiltersModal';
import { TransactionsBreakdown } from './components/TransactionsBreakdown';

const SIGNABLE_STATUS = [OperationStatus.CREATED];

const isFiltersActive = (filters: OperationsFilterParams) =>
	!!(
		filters.dateFrom ||
		filters.dateTo ||
		filters.paymentType ||
		filters.documentNumber ||
		filters.senderIban ||
		filters.beneficiaryIban ||
		filters.beneficiaryName
	);

export const SignPaymentsPage: FC = () => {
	const { t } = useTranslation('', { keyPrefix: 'signPayments' });
	const { t: tTfa } = useTranslation('', { keyPrefix: 'tfa' });
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();

	const [selection, setSelection] = useState<Transaction[]>([]);
	const [signingResult, setSigningResult] = useState<'success' | 'error' | undefined>();
	const [errorSignal, setErrorSignal] = useState(0);
	const [filters, setFilters] = useState<OperationsFilterParams>({});
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

	const filterForm = useForm<OperationsFilterParams>({ defaultValues: {} });

	const { data, isLoading } = useOperations({
		status: SIGNABLE_STATUS,
		page: currentPage,
		limit: pageSize,
		...filters,
	});

	const initiateSigning = useInitiatePaymentSigning();
	const signPayment = useSignPayment();
	const deleteTransactionsMultiple = useDeleteTransactionsMultiple();

	const selectedIds = selection.map(({ id }) => id);
	const { columns, renders, showHeader } = useSignOperationsTable();

	const totalCount = data?.pagination?.count;
	const totalCountLabel = totalCount
		? totalCount > 9
			? '9+'
			: String(totalCount)
		: null;

	const handleFiltersSubmit = (params: OperationsFilterParams) => {
		setFilters(params);
		setCurrentPage(1);
		setSelection([]);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		setSelection([]);
	};

	const handleSelectionChange = (newSelection: Transaction[]) => {
		setSelection(newSelection);
		setSigningResult(undefined);
	};

	const requestOtp = () => {
		setSigningResult(undefined);
		return initiateSigning.mutateAsync({ type: 'multiple', transactionIds: selectedIds });
	};

	const handleConfirm = (code: string) => {
		signPayment
			.mutateAsync({ code, target: { type: 'multiple', transactionIds: selectedIds } })
			.then(async () => {
				setSigningResult('success');
				setSelection([]);
				await queryClient.invalidateQueries({ queryKey: ['operations'] });
			})
			.catch(() => {
				setSigningResult('error');
				setErrorSignal((s) => s + 1);
			});
	};

	const handleDelete = async () => {
		await deleteTransactionsMultiple.mutateAsync({ transactionIds: selectedIds });
		cancelSelection();
		setSelection([]);
		await queryClient.invalidateQueries({ queryKey: ['operations'] });
	};

	const cancelSelection = () => setIsDeleteConfirmOpen(false);

	return (
		<PageContainer
			backUrl="/operations"
			title={
				<>
					{t('title')}
					{totalCountLabel && (
						<span className="text-neutral-600 font-normal"> | {totalCountLabel}</span>
					)}
				</>
			}
		>
			<div className="flex flex-wrap items-center gap-space-8 mb-space-40 max-mobile:mb-space-24">
				<FormProvider {...filterForm}>
					<OperationsFiltersModal
						compact
						hasActiveFilters={isFiltersActive(filters)}
						onFiltersSubmit={handleFiltersSubmit}
					/>
				</FormProvider>
			</div>

			{isLoading ? (
				<div className="h-100 flex justify-center items-center">
					<Loader />
				</div>
			) : data?.items?.length ? (
				<Table
					columns={columns}
					getRowKey={(row) => row.id}
					onRowClick={({ id }) => navigate(`/payments/${id}`)}
					onSelectionChange={handleSelectionChange}
					renders={renders}
					rows={data.items}
					selection={selection}
					selectionLabel={t('table.select')}
					showHeader={showHeader}
				/>
			) : (
				<EmptyState
					icon={<SignIcon />}
					subTitle={t('empty.subTitle')}
					title={t('empty.title')}
				/>
			)}

			{data?.pagination && data.pagination.count > pageSize && (
				<Pagination
					className="mt-space-24!"
					currentPage={currentPage}
					dropdownOpenPosition={DropdownPositions.TOP}
					onPageChange={handlePageChange}
					onPageSizeChange={handlePageSizeChange}
					pageOptions={pageOptions}
					pageSize={pageSize}
					pageText={pageText}
					totalCount={data.pagination.count}
					totalCountText={totalCountText}
				/>
			)}

			{selection.length > 0 && (
				<div className="mt-space-40 flex flex-col gap-space-40">
					<TransactionsBreakdown selection={selection} />

					<div className="max-w-125">
						{signingResult === 'success' ? (
							<Alert type="success">{tTfa('alerts.signed')}</Alert>
						) : (
							<>
								{signingResult === 'error' && (
									<Alert type="error">{tTfa('alerts.failed')}</Alert>
								)}
								<TfaSigning
									buttons={
										<Button
											disabled={deleteTransactionsMultiple.isPending}
											onClick={() => setIsDeleteConfirmOpen(true)}
											variant="outlined"
										>
											{t('delete')}
										</Button>
									}
									errorSignal={errorSignal}
									isConfirmDisabled={signPayment.isPending}
									isRequestDisabled={initiateSigning.isPending || signPayment.isPending}
									onConfirm={handleConfirm}
									requestOtp={requestOtp}
									submitButtonProps={{ children: tTfa('buttons.sign') }}
								/>
							</>
						)}
					</div>
				</div>
			)}

			<DeleteConfirmModal
				description={t('deleteModal.description', { count: selectedIds.length })}
				isOpened={isDeleteConfirmOpen}
				isPending={deleteTransactionsMultiple.isPending}
				onCancel={cancelSelection}
				onConfirm={handleDelete}
			/>
		</PageContainer>
	);
};
